import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';

import ApiService, { CotacaoTributoItem } from '../../../../../services/ApiService';
import { OrderSchemaType } from '../order.schema';

export interface Natureza {
  taxConfigId: number;
  nome: string | null;
  cfop: string | null;
  operationType: string;
  // Decimal do Prisma chega como string no JSON.
  aliquotaPIS: string | null;
  aliquotaCOFINS: string | null;
  ncmsCobertos: number[];
  ncmsDescobertos: number[];
}

// Contrato substitutivo do POST /tax-config/cotacao (D4): impostos já resolvidos pelo
// backend, sem taxConfig/taxScenario crus. `valores` (base × alíquota) não é usado aqui —
// a projeção da proposta recalcula a partir do valor do item no form.
type CotacaoItem = CotacaoTributoItem;

const numero = (v: unknown): number | undefined =>
  v === null || v === undefined || v === '' ? undefined : Number(v);

/**
 * Base de cálculo da projeção: o mesmo valor que o PDF imprime como total do item.
 * Sem preço não há o que projetar — devolve null e a linha sai com traço.
 */
export const valorDoItem = (item: any): number | null => {
  const direto = numero(item?.sellingPrice) ?? numero(item?.itemPrice);
  if (direto !== undefined && direto > 0) return direto;

  const subtotal = numero(item?.priceDetails?.itemSubtotal);
  if (subtotal !== undefined && subtotal > 0) return subtotal;

  return null;
};

export function useTaxProjection() {
  const { control, setValue, getValues } = useFormContext<OrderSchemaType>();

  const supplierId = useWatch({ control, name: 'supplierId' });
  const clientDetails = useWatch({ control, name: 'clientDetails' }) as any;
  const supplierDetails = useWatch({ control, name: 'supplierDetails' }) as any;
  const taxConfigId = useWatch({ control, name: 'taxConfigId' });
  const items = useWatch({ control, name: 'items' }) as any[] | undefined;

  const [naturezas, setNaturezas] = useState<Natureza[]>([]);
  const ultimaCotacao = useRef<string>('');

  const stateId: number | undefined =
    clientDetails?.Address?.Neighborhood?.City?.stateId ??
    clientDetails?.Address?.Neighborhood?.City?.State?.id;

  const ufDestino: string | undefined =
    clientDetails?.Address?.Neighborhood?.City?.State?.uf;

  const emissor: string | undefined = supplierDetails?.cnpj ?? supplierDetails?.companyName;

  const ncmIds = useMemo(
    () =>
      Array.from(
        new Set(
          (items ?? [])
            .map((i) => numero(i?.ncmId))
            .filter((v): v is number => v !== undefined),
        ),
      ),
    [items],
  );

  const bloqueio = useMemo(() => {
    if (!supplierId) {
      return 'Selecione a empresa em "Meu CNPJ" para liberar a projeção de tributos.';
    }
    if (!stateId) {
      return 'O cliente não tem endereço completo — sem o estado de destino não há como resolver o cenário fiscal. Complete o cadastro do cliente.';
    }
    return null;
  }, [supplierId, stateId]);

  // Naturezas aplicáveis: recarrega quando muda o conjunto de NCMs, a UF ou o emissor.
  useEffect(() => {
    if (bloqueio || ncmIds.length === 0 || !stateId) {
      setNaturezas([]);
      return;
    }
    let ativo = true;
    ApiService.fiscal
      .getNaturezas({
        ncmIds: ncmIds.join(','),
        stateId,
        companyEmissorId: supplierId as number,
      })
      .then((res) => {
        if (!ativo) return;
        const data = res.data?.naturezas ?? res.data?.data?.naturezas ?? [];
        setNaturezas(Array.isArray(data) ? data : []);
      })
      .catch(() => {
        if (ativo) setNaturezas([]);
      });
    return () => {
      ativo = false;
    };
  }, [bloqueio, ncmIds.join(','), stateId, supplierId]);

  // Cotação: só dispara quando a chave tributária muda de verdade, e nunca sobrescreve
  // alíquota que o vendedor já editou.
  useEffect(() => {
    if (bloqueio || !taxConfigId || !stateId) return;

    // Item de pedido novo ainda não tem `id`. O orderItemId da cotação é só chave de
    // correlação — negativo nunca colide com id real de item já persistido.
    const alvos = (items ?? [])
      .map((item, indice) => ({ item, indice, chaveItem: Number(item?.id ?? -(indice + 1)) }))
      .filter(({ item }) => numero(item?.ncmId) !== undefined);

    if (alvos.length === 0) return;

    // PIS/COFINS não vêm do cenário por UF: são alíquota da natureza, iguais para todo o pedido.
    // Entram na chave para o efeito reagir quando a lista de naturezas chega depois do taxConfigId.
    const natureza = naturezas.find((n) => n.taxConfigId === taxConfigId);
    const pisDaNatureza = numero(natureza?.aliquotaPIS);
    const cofinsDaNatureza = numero(natureza?.aliquotaCOFINS);

    // A natureza manda quando tem valor; sem cadastro, preserva o que o vendedor digitou.
    const tributosDaNatureza = (atual: any) => ({
      aliquotaPIS: pisDaNatureza ?? numero(atual?.aliquotaPIS),
      aliquotaCOFINS: cofinsDaNatureza ?? numero(atual?.aliquotaCOFINS),
    });

    const chave = `${taxConfigId}:${stateId}:${supplierId}:${pisDaNatureza ?? ''}:${
      cofinsDaNatureza ?? ''
    }:${alvos
      .map(({ item, chaveItem }) => `${chaveItem}-${item.ncmId}`)
      .join(',')}`;
    if (chave === ultimaCotacao.current) return;
    ultimaCotacao.current = chave;

    let ativo = true;
    ApiService.fiscal
      .cotarTributos({
        taxConfigId: taxConfigId as number,
        stateId,
        companyEmissorId: supplierId as number,
        itens: alvos.map(({ item, chaveItem }) => {
          const base = valorDoItem(item);
          return {
            orderItemId: chaveItem,
            ncmId: Number(item.ncmId),
            ...(base !== null ? { valorTotal: String(base) } : {}),
          };
        }),
      })
      .then((res) => {
        if (!ativo) return;
        // Mantém o fallback de resposta com um nível extra de `.data` (padrão de outras
        // rotas do gateway fiscal) — o `as any` é só nesse desvio, o tipo do método continua o novo shape.
        const cotados: CotacaoItem[] = res.data?.itens ?? (res.data as any)?.data?.itens ?? [];
        const porItem = new Map(cotados.map((c) => [c.orderItemId, c]));
        const agora = new Date().toISOString();

        alvos.forEach(({ item, indice, chaveItem }) => {
          const cotado = porItem.get(chaveItem);
          if (!cotado) return;

          const atual = getValues(`items.${indice}.tax` as any) as any;

          if (!cotado.resolvido) {
            setValue(
              `items.${indice}.tax` as any,
              {
                ...(atual ?? {}),
                ...tributosDaNatureza(atual),
                taxScenarioId: undefined,
                motivoNaoResolvido: cotado.motivo,
                origemIpi: undefined,
                resolvidoEm: agora,
              },
              { shouldDirty: false },
            );
            return;
          }

          const resolvidas = {
            aliquotaICMSResolvida: numero(cotado.impostos?.icms.aliquota),
            aliquotaIPIResolvida: numero(cotado.impostos?.ipi.aliquota),
            aliquotaIBSResolvida: numero(cotado.impostos?.ibs.aliquota),
            aliquotaCBSResolvida: numero(cotado.impostos?.cbs.aliquota),
          };

          // Alíquota efetiva só é sobrescrita quando o vendedor não a editou:
          // editado é exatamente `efetivo !== resolvido` do snapshot anterior.
          const manter = (
            efetivo: number | undefined,
            resolvidoAnterior: number | undefined,
          ) =>
            efetivo !== undefined &&
            resolvidoAnterior !== undefined &&
            efetivo !== resolvidoAnterior;

          setValue(
            `items.${indice}.tax` as any,
            {
              taxScenarioId: cotado.taxScenarioId,
              motivoNaoResolvido: undefined,
              ...resolvidas,
              ...tributosDaNatureza(atual),
              aliquotaICMS: manter(atual?.aliquotaICMS, atual?.aliquotaICMSResolvida)
                ? atual.aliquotaICMS
                : resolvidas.aliquotaICMSResolvida,
              // IPI sem override na proposta (D2): a regra por NCM manda, sempre a resolvida.
              aliquotaIPI: resolvidas.aliquotaIPIResolvida,
              aliquotaIBS: manter(atual?.aliquotaIBS, atual?.aliquotaIBSResolvida)
                ? atual.aliquotaIBS
                : resolvidas.aliquotaIBSResolvida,
              aliquotaCBS: manter(atual?.aliquotaCBS, atual?.aliquotaCBSResolvida)
                ? atual.aliquotaCBS
                : resolvidas.aliquotaCBSResolvida,
              origemIpi: cotado.impostos?.ipi.origem,
              resolvidoEm: agora,
            },
            { shouldDirty: false },
          );
        });
      })
      .catch(() => {
        // Falha de cotação não trava o pedido: a seção fica com os campos abertos.
        ultimaCotacao.current = '';
      });

    return () => {
      ativo = false;
    };
  }, [bloqueio, taxConfigId, stateId, supplierId, items, naturezas, getValues, setValue]);

  const valorDoItemCb = useCallback(valorDoItem, []);

  // Escolher, reescolher (mesma natureza) ou limpar: os três casos zeram a projeção dos
  // itens e resetam a chave de dedup, forçando o efeito acima a cotar do zero. Limpar manda
  // `null` (removido de verdade); escolher manda `undefined` (recotado pelo efeito).
  const escolherNatureza = useCallback(
    (novoTaxConfigId: number | undefined) => {
      const itensAtuais = (getValues('items') as any[] | undefined) ?? [];
      itensAtuais.forEach((item, indice) => {
        if (item?.ncmId == null) return;
        setValue(`items.${indice}.tax` as any, novoTaxConfigId === undefined ? null : undefined, {
          shouldDirty: true,
        });
      });
      ultimaCotacao.current = '';
      setValue('taxConfigId', novoTaxConfigId, { shouldDirty: true });
      setValue('taxConfigCleared', novoTaxConfigId === undefined, { shouldDirty: true });
    },
    [getValues, setValue],
  );

  return {
    naturezas,
    emissor,
    ufDestino,
    bloqueio,
    valorDoItem: valorDoItemCb,
    escolherNatureza,
  };
}
